import { useEffect, useState } from "react";
import API, { ImportWizAPI,BlobAPI } from "../services/api";

interface Props {
  tbv: number | null;
  projectId: number;
  sessionNo: number;
  selectedNode: any;
  onAdded?: () => void;
}

const ImportWizPanel = ({
  tbv,
  projectId,
  sessionNo,
  selectedNode,
  onAdded,
}: Props) => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingKey, setAddingKey] = useState<string | null>(null);

  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [workGroupsMap, setWorkGroupsMap] = useState<Record<string, any[]>>({});
  const [selectedWG, setSelectedWG] = useState<Record<string, number[]>>({});
  const [activeItemKey, setActiveItemKey] = useState<string | null>(null);

  const [selectedLinkIds, setSelectedLinkIds] = useState<number[]>([]);
  const [selectedSubMarks, setSelectedSubMarks] = useState<number[]>([]);
  const [selectedChildIds, setSelectedChildIds] = useState<number[]>([]);
  const [addingGroup, setAddingGroup] = useState<"link" | "sub" | null>(null);

  const [childrenMap, setChildrenMap] = useState<Record<number, any[]>>({});
  const [loadingChildrenMap, setLoadingChildrenMap] = useState<Record<number, boolean>>(
    {}
  );
  const [alreadyLinkedParts, setAlreadyLinkedParts] = useState<any[]>([]);
  const [loadingAlreadyLinkedParts, setLoadingAlreadyLinkedParts] = useState(false);


  const [imageMap, setImageMap] = useState<Record<number, string>>({});
  const [wbsImage, setWbsImage] = useState("");

  const getRowKey = (item: any) =>
    `${selectedNode?.tag ?? "none"}-${item.id}`;

  // ================================
  // LOAD DATA
  // ================================
  useEffect(() => {
    if (tbv === null) {
      setData([]);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        const res = await ImportWizAPI.get(tbv);

        res.sort((a: any, b: any) => {
          const aIsLink = (a.name || "").toLowerCase().includes("link to");
          const bIsLink = (b.name || "").toLowerCase().includes("link to");

          if (!aIsLink && bIsLink) return -1;
          if (aIsLink && !bIsLink) return 1;

          return (a.name || "").localeCompare(b.name || "");
        });

        setData([...res]);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tbv]);

  useEffect(() => {
    setSelectedLinkIds([]);
    setSelectedSubMarks([]);
    setSelectedChildIds([]);
    setExpandedKey(null);
    setWorkGroupsMap({});
    setSelectedWG({});
    setActiveItemKey(null);
    setChildrenMap({});
    setLoadingChildrenMap({});
  }, [tbv]);

  useEffect(() => {
    setSelectedSubMarks([]);
    setSelectedChildIds([]);
    setSelectedWG({});
    setExpandedKey(null);
    setActiveItemKey(null);
    setWorkGroupsMap({});
    setChildrenMap({});
    setLoadingChildrenMap({});
    setAlreadyLinkedParts([]);
    loadAlreadyLinkedParts();
  }, [selectedNode]);

   

 useEffect(() => {
  const loadImages = async () => {
    const items = data.filter(
      (x) => !(x.name || "").toLowerCase().includes("link to")
    );

    const newMap: Record<number, string> = {};

    for (const item of items) {
      try {
        
        
        const notationNo = Math.abs(getNotationId(item));


const imageUrl = await BlobAPI.getImage(
  "Notation",
  notationNo
);
console.log("NotationNo:", notationNo);
console.log("Image URL:", imageUrl);

newMap[notationNo] = imageUrl;

      } catch (err) {
        console.log("No image found", err);
      }
    }

    setImageMap(newMap);
  };

  loadImages();
}, [data]);

useEffect(() => {
  const loadWbsImage = async () => {
    if (!selectedNode) return;

    // Only WBS nodes
    if (selectedNode.tag < 0) return;

    try {
      const imageUrl = await BlobAPI.getImage(
        "WorkTree",
        selectedNode.wbsNo
      );

      console.log("WBS Image:", imageUrl);

      setWbsImage(imageUrl);
    } catch {
      setWbsImage("");
    }
  };

  loadWbsImage();
}, [selectedNode]);
/* useEffect(() => {
  const loadImages = async () => {
    const newMap: Record<number, string> = {};

    for (const item of data) {
      try {
        if (selectedNode?.tag < 0) {
          // Notation
          const notationNo = Math.abs(getNotationId(item));

          const imageUrl = await BlobAPI.getImage(
            "Notation",
            notationNo
          );

          newMap[notationNo] = imageUrl;
        } else {
          // WBS
          const wbsNo = Math.abs(item.id);

          const imageUrl = await BlobAPI.getImage(
            "WorkTree",
            wbsNo
          );

          newMap[wbsNo] = imageUrl;
        }
      } catch (err) {
        console.log("No image found", err);
      }
    }

    setImageMap(newMap);
  };

  loadImages();
}, [data, selectedNode]);
 */

 
  // ================================
  // LOAD DATA FOR WBS / NOTATION
  // ================================
  const linkToItems = data.filter((x) =>
    (x.name || "").toLowerCase().includes("link to")
  );

  const remainingItems = data.filter(
    (x) => !(x.name || "").toLowerCase().includes("link to")
  );

  // ================================
  // HELPERS FOR NOTATION TREE
  // ================================
  const getNotationId = (row: any) =>
    Number(row.NotationNo ?? row.notationNo ?? row.id ?? row.NotationId ?? 0);

  const getNotationText = (row: any) =>
    row?.NotationText ??
    row?.notationText ??
    row?.Notation ??
    row?.notation ??
    row?.name ??
    "";

  const findNotationItemById = (id: number): any | undefined => {
    const direct = remainingItems.find(
      (x: any) => Math.abs(Number(x.id)) === id || Number(x.id) === id
    );

    if (direct) return direct;

    const visited = new Set<number>();

    const searchChildren = (parentId: number): any | undefined => {
      if (visited.has(parentId)) return undefined;
      visited.add(parentId);

      const kids = childrenMap[parentId] ?? [];
      for (const kid of kids) {
        const kidId = getNotationId(kid);
        if (kidId === id) return kid;

        const found = searchChildren(kidId);
        if (found) return found;
      }

      return undefined;
    };

    for (const parentId of Object.keys(childrenMap).map(Number)) {
      const found = searchChildren(parentId);
      if (found) return found;
    }

    return undefined;
  };

  const loadChildren = async (parentNotationNo: number) => {
    const parentId = Math.abs(parentNotationNo);

    if (
      Object.prototype.hasOwnProperty.call(childrenMap, parentId) ||
      loadingChildrenMap[parentId]
    ) {
      return;
    }

    try {
      setLoadingChildrenMap((prev) => ({ ...prev, [parentId]: true }));

      const res = await API.post("/sql/select", {
        Table: "Notations",
        Filters: {
          Project: 11,
          IsDeleted: 0,
          ParentNotation: parentId,
        },
      });

      setChildrenMap((prev) => ({
        ...prev,
        [parentId]: res.data || [],
      }));
    } catch (err) {
      console.error(err);
      setChildrenMap((prev) => ({
        ...prev,
        [parentId]: [],
      }));
    } finally {
      setLoadingChildrenMap((prev) => ({ ...prev, [parentId]: false }));
    }
  };

  // ================================
  // WBS ITEM CLICK LOGIC
  // ================================
  const handleSelectItem = async (item: any, rowKey: string) => {
    setActiveItemKey(rowKey);

    const isNegativeItem = item.id < 0;

    if (!isNegativeItem) {
      setExpandedKey(null);
      return;
    }

    if (expandedKey === rowKey) {
      setExpandedKey(null);
      return;
    }

    try {
      const setno = Math.abs(item.id);

      const wgDT = await ImportWizAPI.getWorkGroups(setno);

      const map = new Map<number, any>();

      wgDT.forEach((w: any) => {
        if (w.Subcontractor === setno && w.Client === setno) {
          if (!map.has(Number(w.WorkGroupNo))) {
            map.set(Number(w.WorkGroupNo), w);
          }
        }
      });

      const wgdta = Array.from(map.values());

      const resw = await API.post("/sql/execsp", {
        sp: "WorkGroupByWBS",
        params: { wbs: selectedNode.wbsNo },
      });
      const wgt = resw.data;

      const res1 = await API.post("/sql/select", {
        Table: "Activity",
        Filters: {},
      });
      const activity = res1.data;

      const processed: any[] = [];

      wgdta.forEach((dg: any) => {
        const wgrow =
          wgt.find(
            (w: any) =>
              w.WGHead === dg.WorkGroupNo && w.WBS === selectedNode.wbsNo
          ) || wgt.find((w: any) => w.WGHead === dg.WorkGroupNo);

        if (wgrow) {
          const act = activity.find(
            (a: any) => a.ActivityNo === wgrow.BillableWork
          );

          if (
            wgrow.BillableWork === 0 ||
            act?.Applicability === "Calculation"
          ) {
            return;
          }

          processed.push({
            workGroupNo: Number(wgrow.WorkGroupNo),
            name: wgrow.WorkGroup,
            color: "gray",
          });
        } else {
          const act = activity.find((a: any) => a.ActivityNo === dg.BillableWork);

          if (dg.BillableWork === 0 || act?.Applicability === "Calculation") {
            return;
          }

          processed.push({
            workGroupNo: Number(dg.WorkGroupNo),
            name: dg.WorkGroup,
            color: "blue",
          });
        }
      });

      setWorkGroupsMap((prev) => ({
        ...prev,
        [rowKey]: processed,
      }));
      setExpandedKey(rowKey);
    } catch (err) {
      console.error(err);
    }
  };

  // ================================
  // WBS ADD LOGIC
  // ================================
  const handleAdd = async (item: any, rowKey: string) => {
    if (!selectedNode) {
      alert("Please select a WBS node");
      return;
    }

    try {
      setAddingKey(rowKey);

      if (item.id > 0) {
        await ImportWizAPI.addToProject({
          tagNo: item.id,
          parentNode: selectedNode.tag,
          project: projectId,
          session: sessionNo,
          name: item.name,
        });

        alert(`Added to ${selectedNode.text} ✅`);
        onAdded?.();

        setSelectedWG({});
        setExpandedKey(null);
        setActiveItemKey(null);
        return;
      }

      const currentSelectedWG = selectedWG[rowKey] || [];

      if (currentSelectedWG.length === 0) {
        alert("Please select at least one Work Group");
        return;
      }

      const payload = {
        mark: Math.abs(item.id),
        WBS2: selectedNode.wbsNo,
        sno: sessionNo,
        WGList: currentSelectedWG.map((wg) => ({
          WGSource: wg,
          WGTarget: -1,
        })),
      };

      await ImportWizAPI.addSetImport(payload);

      const currentWorkGroups = workGroupsMap[rowKey] || [];

      const selectedNames = currentSelectedWG
        .map((id) => {
          const wg = currentWorkGroups.find((w) => w.workGroupNo === id);
          return wg?.name;
        })
        .filter(Boolean)
        .join(", ");

      alert(`Imported: ${selectedNames} → ${selectedNode.text} ✅`);

      onAdded?.();

      setSelectedWG({});
      setExpandedKey(null);
      setActiveItemKey(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data || err.message;
      alert("❌ " + msg);
    } finally {
      setAddingKey(null);
    }
  };

  // ================================
  // NOTATION SELECT HELPERS
  // ================================
  const toggleLinkId = (id: number) => {
    setSelectedLinkIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSubMark = (id: number) => {
    setSelectedSubMarks((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleChildId = (id: number) => {
    setSelectedChildIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAddSelectedLinks = async () => {
    if (!selectedNode) {
      alert("Please select a notation node");
      return;
    }

    if (selectedLinkIds.length === 0) {
      alert("Please select at least one Link To item");
      return;
    }

    try {
      setAddingGroup("link");

      for (const id of selectedLinkIds) {
        const item = linkToItems.find((x) => x.id === id);
        if (!item) continue;

        await ImportWizAPI.addToProject({
          tagNo: item.id,
          parentNode: selectedNode.tag,
          project: projectId,
          session: sessionNo,
          name: item.name,
        });
      }

      alert(`Link To items added under ${selectedNode.text} ✅`);
      onAdded?.();
      setSelectedLinkIds([]);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data || err.message;
      alert("❌ " + msg);
    } finally {
      setAddingGroup(null);
    }
  };

  const handleAddSubParts = async () => {
    if (!selectedNode) {
      alert("Please select a notation node");
      return;
    }

    const parentMark =
      selectedNode.notationNo ??
      (selectedNode.tag < 0 ? Math.abs(selectedNode.tag) : null);

    if (!parentMark) {
      alert("Invalid parent notation");
      return;
    }

    const allSelectedIds = Array.from(
      new Set([...selectedSubMarks, ...selectedChildIds])
    );

    if (allSelectedIds.length === 0) {
      alert("Please select at least one sub part");
      return;
    }

    try {
      setAddingGroup("sub");

      const payload = {
        mark: parentMark,
        sno: sessionNo,
        MarkIds: allSelectedIds.map((id) => ({
          MarkId: Math.abs(id),
        })),
      };

      await ImportWizAPI.addSubSetImport(payload);

      const selectedNames = allSelectedIds
        .map((id) => {
          const item = findNotationItemById(id);
          return item?.name ?? getNotationText(item);
        })
        .filter(Boolean)
        .join(", ");

      alert(`Imported: ${selectedNames} → ${selectedNode.text} ✅`);

      onAdded?.();
      setSelectedSubMarks([]);
      setSelectedChildIds([]);
      setChildrenMap({});
      setLoadingChildrenMap({});
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data || err.message;
      alert("❌ " + msg);
    } finally {
      setAddingGroup(null);
    }
  };

  const loadAlreadyLinkedParts = async () => {
    if (!selectedNode) return;

    const mark =
      selectedNode.notationNo ??
      (selectedNode.tag < 0 ? Math.abs(selectedNode.tag) : null);

    if (!mark) {
      setAlreadyLinkedParts([]);
      return;
    }

    try {
      setLoadingAlreadyLinkedParts(true);

      const res = await ImportWizAPI.getAlreadyLinkedParts(mark);

      setAlreadyLinkedParts(res || []);
    } catch (err) {
      console.error(err);
      setAlreadyLinkedParts([]);
    } finally {
      setLoadingAlreadyLinkedParts(false);
    }
  };

  // ================================
  // NOTATION RENDER
  // ================================
  const renderNotationChildren = (parentNotationNo: number, level = 0) => {
    const parentId = Math.abs(parentNotationNo);
    const children = childrenMap[parentId] ?? [];
    const isLoading = loadingChildrenMap[parentId];
    const hasLoaded = Object.prototype.hasOwnProperty.call(childrenMap, parentId);

    if (isLoading && !hasLoaded) {
      return (
        <div className={level === 0 ? "mt-3" : "mt-2 ml-4 border-l pl-3"}>
          <div className=" text-gray-500 px-3 py-2">
            Loading related items...
          </div>
        </div>
      );
    }

    if (hasLoaded && children.length === 0) {
      return (
        <div className={level === 0 ? "mt-3" : "mt-2 ml-4 border-l pl-3"}>
          <div className=" text-gray-400 px-3 py-2">No related items</div>
        </div>
      );
    }

    if (!children.length) return null;

    return (
      <div className={level === 0 ? "mt-3" : "mt-2 ml-4 border-l pl-3"}>
        {children.map((child: any) => {
          const childId = getNotationId(child);
          const childKey = Math.abs(childId);
          const hasChildren = Object.prototype.hasOwnProperty.call(
            childrenMap,
            childKey
          )
            ? (childrenMap[childKey] ?? []).length > 0
            : false;
          const isChecked = selectedChildIds.includes(childId);

          return (
            <div key={childId} className="mb-2">
              <div
                className={`flex items-start gap-3 border rounded-lg px-3 py-2 bg-white ${
                  isChecked ? "border-indigo-400" : "border-gray-200"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={async () => {
                    const nextChecked = !isChecked;
                    toggleChildId(childId);

                    if (nextChecked) {
                      await loadChildren(childKey);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="mt-1"
                />

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* <span className="font-medium text-sm"> */}
                    <span>
                      {getNotationText(child)}
                    </span>
                    {/* <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded"> */}
                    <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                      {/* Notation No: {childId} */}
                    </span>
                  </div>
                  {/* <p className="text-xs text-gray-500"> */}
                   <p className=" text-gray-500">
                    {child.description}</p>
                </div>
              </div>

              {isChecked && hasChildren && renderNotationChildren(childId, level + 1)}
              {isChecked && !hasChildren && (
                // <div className="ml-4 mt-1 text-[11px] text-gray-400">
                <div className="ml-4 mt-1 text-gray-400">
                  No deeper items
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderNotationView = () => {
    return (
      <div className="flex flex-col gap-4">

      

        <div className=" border rounded-xl p-4 bg-[#fbfbfe]">
          {/* <h3 className="font-semibold mb-3">Sub Parts</h3> */}
          <h3 className="mb-3">Sub Parts</h3>

          <div className="flex flex-col gap-2">
            {remainingItems.map((item) => {
              const isChecked = selectedSubMarks.includes(item.id);
              const parentId = Math.abs(item.id);
              const hasLoadedChildren = Object.prototype.hasOwnProperty.call(
                childrenMap,
                parentId
              );
              const hasChildren =
                hasLoadedChildren && (childrenMap[parentId] ?? []).length > 0;

              return (
                <div
                  key={item.id}
                  className={`border rounded-lg bg-white overflow-hidden ${
                    isChecked ? "border-indigo-400" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-start gap-3 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={async () => {
                        const nextChecked = !isChecked;

                        setSelectedSubMarks((prev) =>
                          nextChecked
                            ? [...prev, item.id]
                            : prev.filter((x) => x !== item.id)
                        );

                        if (!nextChecked) {
                          setSelectedChildIds([]);
                        } else {
                          await loadChildren(parentId);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1"
                    />

                   {/*  <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{item.name}</p>
                        <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                          Set No: {Math.abs(item.id)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{item.description}</p>
                    </div> */}
                    <div className="min-w-0 flex-1">
  <div className="flex items-center gap-3">

   

{/* <div>
 

  <img
    src={imageMap[Math.abs(getNotationId(item))]}
    alt=""
    width={80}
    height={80}
    style={{ border: "2px solid red" }}
    onLoad={() =>
      console.log("IMAGE LOADED", Math.abs(getNotationId(item)))
    }
    onError={() =>
      console.log("IMAGE FAILED", Math.abs(getNotationId(item)))
    }
  />
</div> */}
{imageMap[Math.abs(getNotationId(item))] && (
  <div>
    <img
      src={imageMap[Math.abs(getNotationId(item))]}
      alt=""
      width={80}
      height={80}
      className="border border-gray-300 rounded"
      onLoad={() =>
        console.log("IMAGE LOADED", Math.abs(getNotationId(item)))
      }
      onError={() =>
        console.log("IMAGE FAILED", Math.abs(getNotationId(item)))
      }
    />
  </div>
)}

    <div>
      <div className="flex items-center gap-2 flex-wrap">
        {/* <p className="font-medium">{item.name}</p> */}
        <p>{item.name}</p>

        <span className=" text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
          {/* Set No: {Math.abs(item.id)} */}
        </span>
      </div>

      {/* <p className="text-xs text-gray-500"> */}
      <p className="text-gray-500">
        {item.description}
      </p>
    </div>

  </div>
</div>



                  </div>

                  {isChecked && hasChildren && (
                    <div className="px-3 pb-3">
                      {renderNotationChildren(parentId)}
                    </div>
                  )}

                  {isChecked && hasLoadedChildren && !hasChildren && (
                    <div className="px-3 pb-3  text-gray-400">
                      No related items
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <button
            onClick={handleAddSubParts}
            disabled={addingGroup === "sub"}
           // className="mt-3 px-3 py-1 bg-[#5f5fea] text-white rounded hover:opacity-90 disabled:opacity-60"
           className="mt-3 px-3 py-1 bg-white text-[#5353b9] border border-[#5353b9] rounded hover:bg-[#f5f5ff] disabled:opacity-60"
          >
            {addingGroup === "sub"
              ? "Adding..."
              : `Add to ${selectedNode?.text || "Selected Node"}`}
          </button>
        </div>

        <div className="border rounded-xl p-4 bg-[#fbfbfe]">
          {/* <h3 className="font-semibold mb-3">Link To</h3> */}
          <h3 className="mb-3">Link To</h3>

          <div className="flex flex-col gap-2">
            {linkToItems.map((item) => {
              const isChecked = selectedLinkIds.includes(item.id);

              return (
                <label
                  key={item.id}
                  className={`flex items-start gap-3 border rounded-lg px-3 py-2 bg-white cursor-pointer ${
                    isChecked ? "border-indigo-400" : "border-gray-200"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleLinkId(item.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-1"
                  />

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* <p className="font-medium">{item.name}</p> */}
                      <p>{item.name}</p>
                      <span className=" text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                        {/* Set No: {Math.abs(item.id)} */}
                      </span>
                    </div>
                    <p className=" text-gray-500">{item.description}</p>
                  </div>
                </label>
              );
            })}
          </div>

          <button
            onClick={handleAddSelectedLinks}
            disabled={addingGroup === "link"}
           // className="mt-3 px-3 py-1 bg-[#5f5fea] text-white rounded hover:opacity-90 disabled:opacity-60"
           className="mt-3 px-3 py-1 bg-white text-[#5353b9] border border-[#5353b9] rounded hover:bg-[#f5f5ff] disabled:opacity-60"
          >
            {addingGroup === "link"
              ? "Adding..."
              : `Add to ${selectedNode?.text || "Selected Node"}`}
          </button>
        </div>

        <div className="border rounded-xl p-4 bg-[#fbfbfe]">
          {/* <h3 className="font-semibold mb-3">Already Linked Parts</h3> */}
          <h3 className="mb-3">Already Linked Parts</h3>

          {loadingAlreadyLinkedParts ? (
            // <div className="text-sm text-gray-500">Loading...</div>
            <div className="text-gray-500">Loading...</div>
          ) : alreadyLinkedParts.length === 0 ? (
            // <div className="text-sm text-gray-400">
            <div className="text-gray-400">
              No already linked parts
              </div>
          ) : (
            <div className="flex flex-col gap-2">
              {alreadyLinkedParts.map((item) => (
                <div
                  key={item.notationNo}
                  className="border rounded-lg px-3 py-2 bg-white"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* <p className="font-medium">{item.notation}</p> */}
                    <p>{item.notation}</p>

                    <span className=" text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                      {/* Notation No: {item.notationNo} */}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ================================
  // WBS RENDER
  // ================================
  const renderWBSView = () => {
    return (
      // <div className="flex flex-col gap-3">

      <div
    className="grid gap-4"
    style={{
      gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
      alignItems: "start",
    }}
  >

         {wbsImage && (
      <div className="border rounded-xl p-2 bg-white">
        <img
          src={wbsImage}
          alt="WBS"
          width={120}
          height={120}
          style={{ border: "1px solid black" }}
        />
      </div>
    )}
        {data.map((item) => {
          const rowKey = getRowKey(item);
          const isActive = activeItemKey === rowKey;
          const currentWorkGroups = workGroupsMap[rowKey] || [];

          return (
            <div
              key={rowKey}
              onClick={() => handleSelectItem(item, rowKey)}
              className={` border rounded-xl p-3 cursor-pointer transition
                ${
                  isActive
                    ? "bg-[#e6e6fa] border-[#a6a6ed]"
                    : "bg-[#fbfbfe] hover:bg-[#f0f0ff]"
                }`}
            >
              <div className="flex justify-between items-center">
                {/* <div>
                  <h3 className="font-semibold">{item.name}</h3>
                  <p className="text-xs text-gray-500">{item.description}</p>
                </div> */}

                <div className="flex items-center gap-3">
{/*  {imageMap[Math.abs(getNotationId(item))] && (
  <img
    src={imageMap[Math.abs(getNotationId(item))]} 
   
    alt={item.name}
    width={80}
    height={80}
    style={{ border: "1px solid black" }}
  />
)} */}

{imageMap[Math.abs(getNotationId(item))] && (
  <img
    src={imageMap[Math.abs(getNotationId(item))]}
    alt={item.name}
    width={80}
    height={80}
    //className="block rounded"
    // style={{ background: "transparent" }}
  />
)}
  <div>
    {/* <h3 className="font-semibold">{item.name}</h3> */}
    <h3 className="font-normal">{item.name}</h3>
    <p className=" text-gray-500">{item.description}</p>

    
  </div>

</div>

                {item.id > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAdd(item, rowKey);
                    }}
                    disabled={addingKey === rowKey}
                    //className="px-3 py-1 bg-[#5f5fea] text-white rounded hover:opacity-90"
                    className="px-3 py-1 bg-white text-[#5353b9] border border-[#5353b9] rounded hover:bg-[#f5f5ff]"
                  >
                    {addingKey === rowKey ? "Adding..." : "Add"}
                  </button>
                )}
              </div>

              {expandedKey === rowKey && item.id < 0 && (
                <div className="mt-3 border-t pt-2">
                  {/* <p className="text-sm text-blue-600 mb-2"> */}
                  <p className="text-blue-600 mb-2">
                    {/* Set No: {Math.abs(item.id)} */}
                  </p>

                  <div className="flex flex-col gap-2">
                    {currentWorkGroups.map((wg, wgIndex) => {
                      const currentSelectedWG = selectedWG[rowKey] || [];
                      const isChecked = currentSelectedWG.includes(wg.workGroupNo);

                      return (
                        <label
                          key={`${rowKey}-${wg.workGroupNo}-${wgIndex}`}
                          className={`flex gap-2 items-center ${
                            wg.color === "gray" ? "text-gray-400" : "text-black"
                          }`}
                        >
                          <input
                            type="checkbox"
                            onClick={(e) => e.stopPropagation()}
                            checked={isChecked}
                            onChange={() =>
                              setSelectedWG((prev) => {
                                const current = prev[rowKey] || [];

                                return {
                                  ...prev,
                                  [rowKey]: current.includes(wg.workGroupNo)
                                    ? current.filter((x) => x !== wg.workGroupNo)
                                    : [...current, wg.workGroupNo],
                                };
                              })
                            }
                          />
                          {wg.name}
                        </label>
                      );
                    })}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAdd(item, rowKey);
                    }}
                    disabled={addingKey === rowKey}
                    //className="mt-3 px-3 py-1 bg-[#5f5fea] text-white rounded hover:opacity-90"
                    className="mt-3 px-3 py-1 bg-white text-[#5353b9] border border-[#5353b9] rounded hover:bg-[#f5f5ff]"
                  >
                    {addingKey === rowKey
                      ? "Adding..."
                      : `Add to ${selectedNode?.text}`}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    // <div className="flex-1 overflow-y-auto p-4">
    <div
  className="flex-1 overflow-y-auto p-4"
  style={{
    fontFamily: "'Segoe UI', sans-serif",
    fontSize: "13px",
    fontWeight: 400,
  }}
>
      {loading ? (
        <p>Loading...</p>
      ) : data.length === 0 ? (
       <p>No Parts or Subparts to import</p>
      ) : selectedNode?.tag < 0 ? (
        renderNotationView()
      ) : (
        renderWBSView()
      )}
    </div>
  );
};

export default ImportWizPanel;